// Per-LINE bin-load selection logic for the POS. Extracted so it is unit-testable and so
// the panel can never fan one cart line out to multiple bins again.
//
// One cart feed line -> one bin, chosen by the cashier. Default the bin only when it is
// unambiguous (one candidate, or the member has a single bin); otherwise leave it
// unassigned. The weight is the product's real size (quantity x size_lbs, BYOC size=1) or
// the weighed sold_weight_lbs — never quantity x 40.

export interface CartLine {
  product: number | null;
  product_name: string;
  quantity: string | number;
  sold_weight_lbs?: string | number | null;
  is_weight_based?: boolean;
}

export interface BinOption {
  id: number;
  name: string;
  feed_category: string;
  feed_category_display?: string;
}

export interface ProductInfo {
  id: number;
  size_lbs?: string | number | null;
}

export interface LineBinSelection {
  lineKey: string;
  productId: number | null;
  productName: string;
  quantity: number;        // true weight in lbs
  binId: number | null;    // chosen bin (default per rules below); null = unassigned
}

// Bin-category -> product-name keywords, used ONLY to pick a default when there is exactly
// one matching bin. It never selects more than one bin.
export const CATEGORY_KEYWORDS: Record<string, string[]> = {
  poultry: ['layer', 'chicken', 'poultry', 'egg', 'broiler', 'chick', 'flock'],
  dog: ['dog', 'canine', 'puppy'],
  cat: ['cat', 'feline', 'kitten'],
  goat: ['goat', 'caprine'],
  sheep: ['sheep', 'lamb', 'ovine'],
  cattle: ['cattle', 'beef', 'cow', 'calf', 'bovine'],
  horse: ['horse', 'equine', 'pony'],
  swine: ['pig', 'swine', 'hog'],
  rabbit: ['rabbit', 'bunny'],
};

function toNum(v: string | number | null | undefined): number {
  if (v == null) return NaN;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return isNaN(n) ? NaN : n;
}

/** True weight in lbs for a line: the weighed figure if present, else quantity x size_lbs.
 *  Returns 0 when it can't be determined (no weight, no size) — such a line is not a feed
 *  line for bin purposes and is dropped from the panel. */
export function lineWeightLbs(line: CartLine, productsById: Record<number, ProductInfo>): number {
  const sold = toNum(line.sold_weight_lbs ?? null);
  if (!isNaN(sold) && sold > 0) return sold;           // weighed / BYOC: authoritative pounds
  const qty = toNum(line.quantity);
  const product = line.product != null ? productsById[line.product] : undefined;
  const size = product ? toNum(product.size_lbs ?? null) : NaN;
  if (!isNaN(qty) && qty > 0 && !isNaN(size) && size > 0) return qty * size;  // bag: qty x size
  return 0;
}

/** Bins whose category keywords appear in the line's product name. */
export function candidateBinsForLine(line: CartLine, bins: BinOption[]): BinOption[] {
  const name = (line.product_name || '').toLowerCase();
  return bins.filter((b) => {
    const kws = CATEGORY_KEYWORDS[(b.feed_category || '').toLowerCase()] || [];
    return kws.some((kw) => name.includes(kw));
  });
}

/** The default bin for a line: the single candidate, or the member's only bin, else null.
 *  Ambiguous (more than one candidate among several bins) -> null (cashier must choose). */
export function defaultBinId(line: CartLine, bins: BinOption[]): number | null {
  if (bins.length === 1) return bins[0].id;            // only one bin -> obvious
  const candidates = candidateBinsForLine(line, bins);
  if (candidates.length === 1) return candidates[0].id; // exactly one category match
  return null;                                          // 0 or >1 candidates -> unassigned
}

/** Build one selection per FEED cart line. Returns [] when the member has no bins so the
 *  panel is absent, not an empty dropdown. */
export function buildBinLoadSelections(
  cartLines: CartLine[],
  bins: BinOption[],
  productsById: Record<number, ProductInfo>,
): LineBinSelection[] {
  if (!bins || bins.length === 0) return [];
  const out: LineBinSelection[] = [];
  cartLines.forEach((line, i) => {
    const weight = lineWeightLbs(line, productsById);
    if (weight <= 0) return;                            // not a determinable feed line
    out.push({
      lineKey: `${line.product ?? 'x'}-${i}`,
      productId: line.product ?? null,
      productName: line.product_name || '',
      quantity: weight,
      binId: defaultBinId(line, bins),
    });
  });
  return out;
}
